import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { BlockGuidePage } from "../components/BlockGuidePage";
import { PageChrome } from "../components/PageChrome";
import { blockGuideBySlug, blockPath } from "../lib/blocking";
import {
  articleJsonLd,
  blockOgImage,
  breadcrumbJsonLd,
  canonical,
  documentHeaders,
  faqJsonLd,
  socialMeta,
} from "../lib/seo";

/**
 * `/block/<app>` — one app.
 *
 * `BlogPosting` rather than `HowTo`, even though this is the one corpus on the
 * site where `HowTo` would be defensible. Google deprecated `HowTo` rich
 * results for desktop and mobile in 2023, so the markup now buys nothing, and
 * the page is more argument than procedure — the steps take four lines and the
 * reason they keep failing takes the rest of it.
 */
export const Route = createFileRoute("/block/$slug")({
  loader: ({ params }) => {
    const guide = blockGuideBySlug(params.slug);
    if (!guide) throw notFound();
    return guide;
  },
  headers: documentHeaders,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const path = blockPath(loaderData.slug);
    return {
      meta: [
        { title: loaderData.title },
        { name: "description", content: loaderData.description },
        ...socialMeta({
          title: loaderData.title,
          description: loaderData.description,
          path,
          lang: "en",
          image: blockOgImage(loaderData.slug),
          imageAlt: loaderData.h1,
        }),
        {
          "script:ld+json": articleJsonLd({
            headline: loaderData.h1,
            description: loaderData.description,
            path,
            published: loaderData.published,
            updated: loaderData.updated,
          }),
        },
        {
          "script:ld+json": breadcrumbJsonLd([
            { name: "Bible Shield", path: "/" },
            { name: "Block an app", path: "/block" },
            { name: loaderData.h1, path },
          ]),
        },
        ...(loaderData.faq
          ? [{ "script:ld+json": faqJsonLd(loaderData.faq) }]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical(path) }],
    };
  },
  component: BlockRoute,
});

function BlockRoute() {
  const guide = Route.useLoaderData();
  return (
    <PageChrome>
      <nav
        aria-label="breadcrumb"
        className="mx-auto mb-6 w-full max-w-[68ch] text-sm text-ink2"
      >
        <Link
          to="/block"
          className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
        >
          block an app
        </Link>
      </nav>
      <BlockGuidePage guide={guide} />
    </PageChrome>
  );
}
